import React, { useState, useEffect, useRef } from 'react';
import { Bell, X, Check } from '@phosphor-icons/react';
import axios from 'axios';
import { useAutoRefresh } from '../hooks/useAutoRefresh';

// Always use current origin for API calls (works for any domain)
const API = `${window.location.origin}/api`;

export default function NotificationBell() {
    const [notifications, setNotifications] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [loading, setLoading] = useState(false);
    const dropdownRef = useRef(null);

    const unreadCount = notifications.filter(n => !n.read).length;

    // Fetch notifications
    const fetchNotifications = async () => {
        try {
            const { data } = await axios.get(`${API}/notifications`);
            setNotifications(data);
        } catch (err) {
            console.error('Error fetching notifications:', err);
        }
    };

    // Initial fetch
    useEffect(() => {
        fetchNotifications();
    }, []);

    // Auto-refresh: 30s poll + visibility/focus + global manual refresh button
    useAutoRefresh(fetchNotifications, { intervalMs: 30000 });

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };

        if (showDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showDropdown]);

    const markAsRead = async (notificationId) => {
        try {
            await axios.put(`${API}/notifications/${notificationId}/read`);
            setNotifications(prev =>
                prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
            );
        } catch (err) {
            console.error('Error marking as read:', err);
        }
    };

    const deleteNotification = async (notificationId) => {
        try {
            await axios.delete(`${API}/notifications/${notificationId}`);
            setNotifications(prev => prev.filter(n => n.id !== notificationId));
        } catch (err) {
            console.error('Error deleting notification:', err);
        }
    };

    const markAllAsRead = async () => {
        setLoading(true);
        try {
            await axios.post(`${API}/notifications/mark-all-read`);
            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
        } catch (err) {
            console.error('Error marking all as read:', err);
        } finally {
            setLoading(false);
        }
    };

    // Click on a notification card → mark as read + navigate to notification.url
    const openNotification = async (notification) => {
        setShowDropdown(false);
        if (!notification.read) {
            // Best-effort — don't block navigation on failure
            markAsRead(notification.id).catch(() => {});
        }
        const target = notification.url;
        if (!target) return;
        try {
            // Same-origin absolute → use history navigation so React Router picks it up
            const url = new URL(target, window.location.origin);
            if (url.origin === window.location.origin) {
                // Update history + fire popstate so client-side routing reacts
                window.history.pushState({}, '', url.pathname + url.search + url.hash);
                window.dispatchEvent(new PopStateEvent('popstate'));
                // React Router with hash-based tab param won't rerun useEffect for
                // query-string-only changes → force one round trip via location for
                // reliability across dashboards.
                window.location.href = url.pathname + url.search + url.hash;
            } else {
                window.open(target, '_blank', 'noopener');
            }
        } catch {
            window.location.href = target;
        }
    };

    const getTimeAgo = (isoDate) => {
        const date = new Date(isoDate);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMins / 60);
        const diffDays = Math.floor(diffHours / 24);

        if (diffMins < 1) return 'teraz';
        if (diffMins < 60) return `${diffMins} min temu`;
        if (diffHours < 24) return `${diffHours} godz. temu`;
        if (diffDays === 1) return '1 dzień temu';
        return `${diffDays} dni temu`;
    };

    return (
        <div className="relative" ref={dropdownRef}>
            {/* Bell Icon with Badge */}
            <button
                onClick={() => setShowDropdown(!showDropdown)}
                className="relative p-2 hover:bg-muted rounded-sm transition-colors"
                data-testid="notification-bell"
            >
                <Bell className="w-5 h-5" weight={unreadCount > 0 ? 'fill' : 'regular'} />
                {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                        {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                )}
            </button>

            {/* Dropdown */}
            {showDropdown && (
                <div className="absolute right-0 mt-2 w-80 md:w-96 bg-white border border-border rounded-sm shadow-lg z-50 max-h-[500px] overflow-hidden flex flex-col">
                    {/* Header */}
                    <div className="p-4 border-b border-border flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Bell className="w-5 h-5" />
                            <h3 className="font-semibold">Powiadomienia</h3>
                            {unreadCount > 0 && (
                                <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                                    {unreadCount}
                                </span>
                            )}
                        </div>
                        <button
                            onClick={() => setShowDropdown(false)}
                            className="p-1 hover:bg-muted rounded-sm"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>

                    {/* Notifications List */}
                    <div className="flex-1 overflow-y-auto">
                        {notifications.length === 0 ? (
                            <div className="p-8 text-center text-muted-foreground">
                                <Bell className="w-12 h-12 mx-auto mb-2 opacity-30" />
                                <p>Brak powiadomień</p>
                            </div>
                        ) : (
                            notifications.map(notification => (
                                <div
                                    key={notification.id}
                                    role={notification.url ? 'button' : undefined}
                                    tabIndex={notification.url ? 0 : undefined}
                                    onClick={notification.url ? () => openNotification(notification) : undefined}
                                    onKeyDown={notification.url ? (e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            openNotification(notification);
                                        }
                                    } : undefined}
                                    className={`p-4 border-b border-border transition-colors ${
                                        notification.url ? 'cursor-pointer hover:bg-blue-50/60' : 'hover:bg-muted/50'
                                    } ${!notification.read ? 'bg-muted/50' : ''}`}
                                    data-testid={`notification-${notification.id}`}
                                >
                                    <div className="flex items-start gap-3">
                                        <div className="flex-shrink-0 mt-1">
                                            {notification.type === 'pdf_uploaded' ? (
                                                <div className="w-8 h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center">
                                                    📄
                                                </div>
                                            ) : notification.type === 'paliwo_unmatched' ? (
                                                <div className="w-8 h-8 bg-amber-100 text-amber-700 rounded-full flex items-center justify-center">
                                                    ⛽
                                                </div>
                                            ) : notification.type === 'employee_registered' ? (
                                                <div className="w-8 h-8 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center">
                                                    👤
                                                </div>
                                            ) : (
                                                <div className="w-8 h-8 bg-muted text-foreground rounded-full flex items-center justify-center">
                                                    💰
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className={`text-sm ${!notification.read ? 'font-semibold' : ''}`}>
                                                {notification.message}
                                            </p>
                                            <p className="text-xs text-muted-foreground mt-1">
                                                {getTimeAgo(notification.created_at)}
                                            </p>
                                            <div className="flex gap-2 mt-2" onClick={(e) => e.stopPropagation()}>
                                                {!notification.read && (
                                                    <button
                                                        onClick={() => markAsRead(notification.id)}
                                                        className="text-xs px-2 py-1 bg-green-600 text-white rounded-sm hover:bg-green-700 flex items-center gap-1"
                                                    >
                                                        <Check className="w-3 h-3" />
                                                        Przeczytane
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => deleteNotification(notification.id)}
                                                    className="text-xs px-2 py-1 text-red-600 hover:bg-red-50 rounded-sm"
                                                >
                                                    ❌
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Footer */}
                    {unreadCount > 0 && (
                        <div className="p-3 border-t border-border">
                            <button
                                onClick={markAllAsRead}
                                disabled={loading}
                                className="w-full text-sm py-2 text-primary hover:bg-primary/10 rounded-sm font-medium transition-colors disabled:opacity-50"
                            >
                                {loading ? 'Oznaczanie...' : 'Oznacz wszystkie jako przeczytane'}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
